import React from "react";
import DeliveryInfo from "../DeliveryInfo";
import { Order } from "../types";
import toast from "react-hot-toast";
import { AdminOrderService } from "../../../../../../services/api/AdminOrderService/admin.order.service";

interface Props {
  setShowGeneratePrescriptionForm: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  selectedOrder: Order | null;
  onOrderCancelled?: (orderId: string) => void;
  orderDetails: () => void;
}

const WithoutPrescriptionMedicineOrderDetails: React.FC<Props> = ({
  setShowGeneratePrescriptionForm,
  selectedOrder,
  onOrderCancelled,
  orderDetails,
}) => {
  const isPrepareOrderDisabled = selectedOrder?.orderStatus !== "PENDING_ADMIN";
  const isOrderCancelled = selectedOrder?.orderStatus === "CANCELLED";

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleCancelOrder = async () => {
    try {
      const response = await AdminOrderService.cancelOrder(
        selectedOrder?.id.toString() || ""
      );

      if (response.success) {
        toast.success(response.message);
        onOrderCancelled?.(selectedOrder?.id.toString() || "");
        orderDetails();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to cancel order.");
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 p-6 rounded-md shadow-sm h-[calc(100vh-64px)] overflow-y-auto"
      style={{
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
      }}
    >
      <div className="mb-4">
        <p className="text-sm mb-2 ">
          Order ID: {selectedOrder?.orderNumber || selectedOrder?.id}
        </p>
        <h2 className="text-lg font-semibold text-gray-900">Order Details</h2>
      </div>

      <div className="space-y-4 mb-6">
        {selectedOrder?.productOrderItems?.map((item: any, index: number) => (
          <div key={index} className="flex items-center gap-4">
            {item.productImage && (
              <img
                src={item.productImage}
                alt={item.productName}
                width={50}
                height={50}
                className="rounded border"
              />
            )}
            <div className="flex-1">
              <p className="font-semibold text-sm text-gray-800">
                {item.productName}
              </p>
              <p className="text-xs text-gray-500">{item.productDescription}</p>
              <p className="text-xs text-gray-500">
                MRP: ₹{item.productPrice?.product_mrp} | Price: ₹
                {item.productPrice?.final_selling_price} |
                {item.productPrice?.selling_discount}% off
              </p>
            </div>
            <div className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium">
              {item.productsItemsCount}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col float-right mt-6 mb-4">
        <p className="text-red-600 font-semibold text-sm">
          {selectedOrder?.isPrescriptionRequired
            ? "Prescription Required"
            : "Doctor Verification Needed"}
        </p>
      </div>

      <DeliveryInfo
        address={selectedOrder?.orderAddress}
        pincode={selectedOrder?.orderPincode}
        orderDate={
          selectedOrder?.orderDate && formatDate(selectedOrder.orderDate)
        }
        orderStatus={selectedOrder?.orderStatus}
        patientId={selectedOrder?.patientId}
        patientDetails={selectedOrder?.patientDetails}
      />

      <div className="mt-6 flex gap-4">
        {/* Only show Generate Prescription button if order is not cancelled */}
        {selectedOrder?.orderStatus !== "CANCELLED" && (
          <button
            disabled={isPrepareOrderDisabled}
            className={`px-4 py-2 rounded-md ${
              isPrepareOrderDisabled
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white"
            }`}
            onClick={() => setShowGeneratePrescriptionForm(true)}
          >
            {selectedOrder?.orderStatus === "PENDING_ADMIN"
              ? "Generate Prescription"
              : selectedOrder?.orderStatus === "PENDING_PATIENT"
              ? "Waiting Patient Confirmation"
              : selectedOrder?.orderStatus === "CLINIC_ASSIGNED"
              ? "At Assigned Clinic"
              : selectedOrder?.orderStatus === "DELIVERY_ASSIGNED"
              ? "Delivery Partner Assigned"
              : selectedOrder?.orderStatus === "OUT_FOR_DELIVERY"
              ? "Out For Delivery"
              : selectedOrder?.orderStatus === "COMPLETED"
              ? "Order Completed"
              : "View Order"}
          </button>
        )}

        {(selectedOrder?.orderStatus === "PENDING_ADMIN" ||
          selectedOrder?.orderStatus === "PENDING_PATIENT") &&
          !isOrderCancelled && (
            <button
              onClick={handleCancelOrder}
              className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white"
            >
              Cancel Order
            </button>
          )}
        {isOrderCancelled && (
          <button
            disabled
            className="px-4 py-2 rounded-md bg-gray-300 cursor-not-allowed"
          >
            Order Cancelled
          </button>
        )}
      </div>
    </div>
  );
};

export default WithoutPrescriptionMedicineOrderDetails;
